"use client";

import { cn } from "@gettactic/helpers";
import type { ElementType, ReactNode } from "react";

interface DetailHeaderBarProps {
  /** Small uppercase label on the left (`REQ-42`, `Reservation`, …). */
  eyebrow: string;
  /** Title revealed inline once the body is scrolled. */
  title: string;
  /** Icon tile shown next to the condensed title. */
  icon?: ReactNode;
  /** Right-hand actions (share, overflow menu, mobile close…). */
  actions?: ReactNode;
  /**
   * Scrolled state: the inline icon + title fade in and a shadow lifts the bar.
   * The bar's height never changes — it lives outside the scroll viewport, so this
   * toggle is purely visual and never alters the scroll geometry (no oscillation,
   * even for items with little content).
   */
  condensed?: boolean;
}

/**
 * Fixed top bar of a detail modal: eyebrow + actions, always present at a
 * constant height. When the body is scrolled it reveals an inline title and a
 * shadow, so it reads as a condensed header floating over the content.
 */
export const DetailHeaderBar = ({
  eyebrow,
  title,
  icon,
  actions,
  condensed = false
}: DetailHeaderBarProps) => (
  <div
    className={cn(
      // shadow only (no border) so the condensed state adds zero layout height —
      // the scroll geometry stays constant and can never oscillate.
      "shrink-0 px-8 pt-4 pb-3 transition-shadow duration-200 max-sm:px-4",
      condensed && "shadow-[0_4px_12px_-4px_rgba(16,36,73,0.12)]"
    )}
  >
    <div className="flex items-center gap-2">
      <span className="shrink-0 font-inter font-semibold text-neutral-600 text-xs uppercase tabular-nums tracking-[0.03em]">
        {eyebrow}
      </span>
      {/* Inline icon + title — fades in only when condensed. Keeps its flex-1
          footprint at all times so the bar layout never shifts. */}
      <div
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2 transition-opacity duration-200",
          condensed ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        {icon}
        <span className="truncate font-mona font-semibold text-[14px] text-neutral-1000">
          {title}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
    </div>
  </div>
);

interface DetailHeaderTitleProps {
  /** Icon tile rendered to the left of the title. */
  icon?: ReactNode;
  title: ReactNode;
  /** Secondary line under the title (request type, reservation kind, …). */
  subtitle?: ReactNode;
  /** `H1` in page mode, `DialogTitle` in modal mode. */
  TitleTag: ElementType;
  /** Chip row rendered under the title (see {@link DetailChip}). */
  children?: ReactNode;
}

/**
 * The large title + chips block. Rendered as the first element inside the scroll
 * body so it scrolls away naturally (the {@link DetailHeaderBar} takes over once
 * it's out of view).
 */
export const DetailHeaderTitle = ({
  icon,
  title,
  subtitle,
  TitleTag,
  children
}: DetailHeaderTitleProps) => (
  <div className="pt-1 pb-1">
    <div className="flex items-center gap-3">
      {icon}
      <div className="min-w-0">
        <TitleTag className="font-mona font-semibold text-[18px] text-neutral-1000 leading-[1.32] tracking-[-0.01em]">
          {title}
        </TitleTag>
        {subtitle ? (
          <div className="font-inter font-medium text-[12.5px] text-neutral-400">
            {subtitle}
          </div>
        ) : null}
      </div>
    </div>

    {children ? (
      <div className="mt-4 flex flex-wrap items-center gap-2">{children}</div>
    ) : null}
  </div>
);

interface DetailChipProps {
  children: ReactNode;
  className?: string;
}

/** Pill used in the {@link DetailHeaderTitle} chip row (requester, office, …). */
export const DetailChip = ({ children, className }: DetailChipProps) => (
  <span
    className={cn(
      "flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 font-inter text-neutral-700 text-sm",
      className
    )}
  >
    {children}
  </span>
);
